'use client';
import cn from 'classnames';

import { EllaState } from '../Ella/Ella';
import { EPCCAddress } from '@/types/User';
import EllaCartShippingBilling from '@/components/common/EllaCartShippingBilling/EllaCartShippingBilling';
import EllaCartSubtotal from '@/components/common/EllaCartSubtotal/EllaCartSubtotal';
import EllaCartProduct, {
  ELLA_RETAILER,
  EllaProductData
} from '@/components/common/EllaCartProduct/EllaCartProduct';
import Text from '@/components/common/Text/Text';
import Button from '@/components/common/Button/Button';

import { useUser } from '@/components/hook/user';
import { useEllaAnalytics } from '@/components/hook/analytics';

import './EllaCartConfirmation.style.scss';
import { ProductStore } from '@/types/Product';
import { useEffect, useMemo, useState } from 'react';
import { getGenericOrder } from '@/util/cart_generic_client';

import analytics from '@/util/analytics';
import EllaCartSkeleton from '../EllaCart/EllaCartSkeleton';
import { SingleMessage } from '../EllaMessages/EllaMessages';
import { usePayment } from '@/components/hook/payment';

interface EllaCartConfirmationProps {
  state: EllaState;
  className?: string;
  shippingVivrelle: string | 'showroom';
  /** Dispatch function to update parent state */
  dispatch: (action: any) => void;
  message: string;
}

function EllaCartConfirmation(props: EllaCartConfirmationProps) {
  const { className = '', state, message } = props;

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [vivrelleOrderItems, setVivrelleOrderItems] = useState<
    EllaProductData[]
  >([]);
  const [revolveOrderItems, setRevolveOrderItems] = useState<EllaProductData[]>(
    []
  );
  const [shippingAddress, setShippingAddress] = useState<EPCCAddress | null>(
    null
  );

  const [bill, setBill] = useState(state.bill);
  const [isLoading, setIsLoading] = useState(true);

  const {
    value: { customer: paymentCustomer, method: paymentMethod },
    actions: { refreshPayment }
  } = usePayment();

  const paymentCard = useMemo(() => {
    // Otherwise use payment method (if defined)
    if (paymentMethod) {
      return paymentMethod.card;
    }

    // If paymentMethod not defined, it's likely a legacy customer whose default source is attached to customer
    if (paymentCustomer) {
      return paymentCustomer.default_source;
    }
  }, [paymentMethod, paymentCustomer]);

  // If payment card is not defined, try calling refresh method from context provider
  useEffect(() => {
    if (!paymentCard) {
      refreshPayment();
    }
  }, [refreshPayment, paymentCard]);

  const { value: user } = useUser();
  const { metadata } = useEllaAnalytics();

  const errorSubmitOrder = `Your order has been placed, but we were unable to complete the ${state.errorSubmitOrder} portion. Those item(s) have been removed and were not charged. The rest of your order below is confirmed.`;

  const setErrorMessageTemporarily = (msg: string) => {
    setErrorMessage(msg);
    setTimeout(() => {
      setErrorMessage('');
    }, 3000);
  };

  useEffect(() => {
    if (state.errorSubmitOrder) {
      analytics.track('Ella AI Order Confirmation - Error Message', {
        ...metadata,
        errorMessage: errorSubmitOrder
      });
    }
  }, [state.errorSubmitOrder]);

  // Fetch orders
  useEffect(() => {
    async function setItems() {
      if (!user || !user.paymentCustomerId) {
        return;
      }
      setIsLoading(true);
      if (state.vivrelleOrder) {
        try {
          const items = state.vivrelleOrder?.orderItems?.map((item) => ({
            name: item.product.name,
            provider: ELLA_RETAILER.VIVRELLE,
            price: item.product.price || 0,
            size: item.product.size || '',
            title: item.product.name,
            score: 0,
            link: item.product.link || `/products/${item.product.slug}`,
            image: item.product.mainImage,
            id: item.product.id,
            category: item.product.category || 'handbag',
            subcategory: '',
            subsubcategory: '',
            retail_price: 0,
            mainImage: item.product.mainImage,
            description: '',
            common_color: '',
            product_type: '',
            item_group_id: item.product.sku
          }));

          setVivrelleOrderItems(items);

          if (!state.vivrelleOrder.isNYCPickup) {
            setShippingAddress(state.vivrelleOrder.shippingAddress);
          }
        } catch (error) {
          console.error('Error in fetch revolve order:', error);
          setErrorMessageTemporarily(
            'An unexpected error occurred. Please try again.'
          );
        }
      }

      if (state.revolveOrder?.id) {
        try {
          const order = await getGenericOrder(state.revolveOrder.id);

          const revolveItems = order.included?.items?.map((item: any) => ({
            name: item.name,
            provider: ELLA_RETAILER.REVOLVE,
            price: item.value.amount,
            size: item.custom_inputs?.size,
            title: item.name,
            score: 0,
            allSizes: [],
            link: item.custom_inputs?.product?.link,
            cj_link: item.custom_inputs?.product?.cj_link,
            image:
              item.custom_inputs?.product?.image ||
              item.custom_inputs?.product?.mainImage ||
              item.custom_inputs?.product?.image_link,
            id: item.id,
            revolveCartId: state.revolveCartId,
            category: item.category || 'handbag',
            subcategory: '',
            subsubcategory: ''
          }));

          setRevolveOrderItems(revolveItems);

          const revolveBill = order.data.meta.display_price;
          setBill((prev) => ({
            ...prev,
            subtotal: revolveBill?.without_tax.amount ?? 0,
            shipping: revolveBill?.shipping.amount ?? 0,
            taxes: revolveBill?.tax.amount ?? 0,
            discountPercentage: revolveBill?.discount.amount ?? 0,
            taxRate: 0
          }));

          if (shippingAddress === null) {
            setShippingAddress(order.data.shipping_address);
          }
        } catch (error) {
          console.error('Error in fetch vivrelle order:', error);
          setErrorMessageTemporarily(
            'An unexpected error occurred. Please try again.'
          );
        }
      }
    }

    setItems();
    setIsLoading(false);
  }, [state.vivrelleOrder]);

  const handleRedirect = (store: ProductStore) => {
    if (store === ProductStore.VIVRELLE) {
      window.location.href = '/';
    } else {
      window.open('https://www.revolve.com', '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return <EllaCartSkeleton />;
  }

  return (
    <>
      {state.errorSubmitOrder ? (
        <SingleMessage
          message={errorSubmitOrder}
          error
        />
      ) : (
        <SingleMessage message={message} />
      )}

      <div className={cn('EllaCartConfirmation', className)}>
        {vivrelleOrderItems.length > 0 && (
          <div>
            <div className='EllaCart-title'>
              <Text
                variant='title-xxs'
                uppercase
              >
                Order Details: Vivrelle
              </Text>
              <Text
                variant='title-xxs-500-upper'
                uppercase
              >
                {state.shippingMethodCopy.includes('NYC Pickup')
                  ? 'ShowRoom Pickup'
                  : state.shippingMethodCopy}
              </Text>
            </div>
            <Text
              className='EllaCart-subheader'
              variant='text-s'
            >
              You&apos;ll receive a confirmation email from Vivrelle with the
              details for your item(s). Please note, if you&apos;re an existing
              member, your new order will be prepared once your pre-paid return
              label for the previous order is scanned and in transit.
            </Text>
            <div className='EllaCart-products'>
              {vivrelleOrderItems.map((item, index) => (
                <EllaCartProduct
                  key={index}
                  product={item}
                />
              ))}
            </div>
          </div>
        )}

        {revolveOrderItems.length > 0 && (
          <div>
            <div className='EllaCart-title'>
              <Text
                variant='title-xxs'
                uppercase
              >
                Order Details: REVOLVE & FWRD
              </Text>
              <Text
                variant='title-xxs-500-upper'
                uppercase
              >
                {state.shippingMethodCopy}
              </Text>
            </div>
            <Text
              className='EllaCart-subheader'
              variant='text-s'
            >
              REVOLVE and/or FWRD will send you a separate email confirming the
              details of your order.
              <br />
              If you need to make changes, please contact the REVOLVE support
              team at{' '}
              <a
                href='https://www.revolve.com/contactus?d=Womens'
                className='Link link-s compact underline'
                rel='noopener noreferrer'
                target='_blank'
              >
                Revolve.com
              </a>{' '}
              or call <span style={{ textWrap: 'nowrap' }}>1-888-442-5830</span>{' '}
              for assistance. You may reach FWRD support at{' '}
              <a
                href='https://www.fwrd.com/fw/content/customercare/customer-care/'
                className='Link link-s compact underline'
                rel='noopener noreferrer'
                target='_blank'
              >
                FWRD.com
              </a>{' '}
              or <span style={{ textWrap: 'nowrap' }}>1-866-434-3169.</span>
            </Text>
            <div className='EllaCart-products'>
              {revolveOrderItems?.map((item, index) => (
                <EllaCartProduct
                  key={index}
                  product={item}
                />
              ))}
            </div>
          </div>
        )}
        <EllaCartSubtotal
          subtotal={bill.subtotal}
          shipping={bill.shipping}
          taxes={bill.taxes}
          hasDiscount={false} // Can be updated?
          discountAmountPercentage={bill.discountPercentage}
        />
        <EllaCartShippingBilling
          addresses={shippingAddress ? [shippingAddress] : []}
          shippingMethod={state.shippingMethodCopy}
          bill={state.bill}
          revolveCartId={state.revolveCartId ?? ''}
          canEdit={false}
          vivrelleCart={vivrelleOrderItems}
          cartItems={revolveOrderItems}
          cardDetails={
            paymentCard
              ? {
                  brand: paymentCard?.brand,
                  last4Digits: paymentCard?.last4
                }
              : null
          }
        />
        <div className='Ella-buttons-container'>
          <Button
            className='small'
            variant='pink-400'
            onClick={() => {
              analytics.track(
                'Ella AI Order Confirmation Back to Vivrelle - Clicked',
                {
                  ...metadata
                }
              );
              handleRedirect(ProductStore.VIVRELLE);
            }}
          >
            Back to Vivrelle
          </Button>
          <Button
            className='small'
            variant='pink-400'
            onClick={() => {
              analytics.track(
                'Ella AI Order Confirmation Explore Revolve - Clicked',
                {
                  ...metadata
                }
              );
              handleRedirect(ProductStore.REVOLVE);
            }}
          >
            Go to Revolve.com
          </Button>
        </div>
        {errorMessage && (
          <Text
            className='error'
            variant='text-s'
          >
            {errorMessage}
          </Text>
        )}
      </div>
    </>
  );
}

export default EllaCartConfirmation;
